/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import {
  ICarbonAvoidanceData,
  IDetailComponentProps,
  IRequestContext,
  ISiteCard
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { Card, Icon, Tooltip } from '@scuf/common';
import CarbonAvoidanceComponent from './CarbonAvoidanceComponent';

interface ITotalEmissionDetailContent extends ISiteCard {
  data: ICarbonAvoidanceData;
}

const CarbonAvoidanceDetailContent = (props: ITotalEmissionDetailContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <div className="carbondetail-performance-detail-container">
      <div>
        <CarbonAvoidanceComponent
          data={data}
          trendChartCategory={t(d.CO2)}
          isCarbonAvoidance={true}
        />
      </div>
    </div>
  );
};

const CarbonAvoidanceWithLoader = withLoadingStatus(
  withErrorStatus(CarbonAvoidanceDetailContent)
);

const PortfolioCarbonAvoidanceWidget = (props: IDetailComponentProps) => {
  const [t] = useTranslation();
  const d = descriptors.DetailedView;
  const e = descriptors.energyManagement;
  const { dataConnector, requestContext } = props;

  const onRefresh = () =>
    dataConnector && dataConnector.actions && dataConnector.actions.getAll();
  const { all, loading, error, status } = dataConnector.data;
  const carbonTotalEmissionsData = (all as unknown) as ICarbonAvoidanceData;
  const isDataAvailable = carbonTotalEmissionsData ? true : false;

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as ICarbonAvoidanceData);

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        setData(carbonTotalEmissionsData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  return (
    <>
      <Card className="emaas-performance-detail-card">
        <Card.Header>
          <span className="emaas-summary-card-header">
            {t(d.CARBONAVOIDANCE)}
            <Tooltip
              element={
                <Icon
                  root="common"
                  name="badge-info"
                  exactSize="0.875rem"
                  className="emaas-summary-card-header-info-icon"
                />
              }
              event="hover"
              content={t(d.TOTALAVOIDANCETOOLTIP)}
              position="right center"
              size="small"
            />
          </span>
        </Card.Header>
        <Card.Content className="detail-summary-card">
          <CarbonAvoidanceWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={statusCode}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(e.LOADING)}
            title={t(e.CONNECTIONERRORTITLE)}
            noDataMessage={t(e.NODATAAVAILABLE)}
            connectionErrorMessage={t(e.CONNECTIONERROR)}
          />
        </Card.Content>
      </Card>
    </>
  );
};

export default PortfolioCarbonAvoidanceWidget;
